import { makeAutoObservable } from "mobx";
import { AxiosInstance, AxiosResponse } from "axios";

import {
  CompanySubsidiary,
  ExtendedShareholderDTO,
  NewShareholderResponse,
  PersonInfo,
  ShareholderDetailsResponseDTO,
  ShareholderField,
  ShareholderPortfolio,
  ShareholdersInfo,
  ShareholderType,
  totalNumberOfSharesOfShareholder,
  totalNumberOfVotesForShareholder,
  transformShareholder,
  UpdateShareholderFieldResponse,
} from "../types/Shareholder";
import { Transaction } from "../types/Transactions";
import { DownloadService } from "../api/DownloadService";
import {
  ApiService,
  downloadFile,
  getResponseFilename,
  isBadRequestError,
  UploadShareholdersExcelResponse,
} from "../api";
import { isNotBlank, removeAllSpaces, removeExtraSpaces } from "../utils/strings";
import AlertMessageStore from "./AlertMessageStore";
import { formatPhoneNumber } from "../utils/phoneNumbers";
import { ParseExcelResponse, ShareholderIdShares } from "../types/excelImportDomain";
import { identity, isNil, max, orderBy, sortBy } from "lodash";
import { CompanyItem, failure, isOk, isPending, ok, pending, Result } from "../common/common";
import { diffDays, toDate } from "../utils/Dates";
import UserStore from "./UserStore";
import { History } from "history";
import {
  ShareholderModel,
  validateAllShareholders,
  ValidationResultForShareholder,
  ValidationResultForShareholderRegister,
} from "../shareholderRegister/shareholders/ShareholderModel";

import { lookupNorwegianCity } from "../utils/postalCodes";
import { navigateToNewTransaction, navigateToShareholdersList } from "../utils/navigation";
import { isCountryCodeNorway, isCountryCodeSweden, norway } from "../utils/countries";
import { DialogStore } from "../dialogs/dialogDomain";
import { TransactionType } from "../types/enums/TransactionType";

import { CURRENCY_NOK } from "../utils/currency";
import { FileUploadConfig } from "../fileUpload/fileUploadDomain";
import { Language } from "../i18n/languages";
import { TFunction, TMessage } from "../i18n/tt";
import { ShareholdingPerDateModel } from "../shareholderRegister/transactions/shareholdingPerDate/shareholdingPerDateDomain";
import { when } from "../utils/functions";
import { NotifyShareholdersStore } from "./NotifyShareholdersStore";
import { isSwedishOrgNo } from "../utils/orgNo";
import {
  LocalDateFieldModel,
  NumberFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "../common/commonForms";
import {
  getShareSumByType,
  getShareSumWithoutCompany,
  ShareholderPledgeForm,
  ShareholderRemarksForm,
  ShareholderStatus,
} from "../shareholderRegister/shareholderRegisterDomain";
import { ShareType, ShareTypeCode, shareTypeLabel } from "../common/share";
import { isNotRegisteredIssue } from "../shareholderRegister/transactions/transactionLogDomain";
import { Actor, actorsApi, ActorType, SelectedActor } from "../types/SelectActors";
import { AIClient } from "../ai/AIAssistant";
import { toShareholderReference } from "../utils/UrnReference";
import { DCEvent } from "../events/eventsDomain";
import {
  ExportFormat,
  ExportShareholderData,
} from "../shareholderRegister/shareholders/ExportShareholdersDomain";
import { ShareholderFollowUpItem } from "../portfolio/followUp/shareholdersFollowUpDomain";
import { IssueOfNewSharesTransactionModel } from "../shareholderRegister/transactions/issue/issueTransactionDomain";
import { validationErrorMessage } from "../utils/Errors";

export function shouldShowInactiveShareholders(shareholders: Array<ExtendedShareholderDTO>) {
  return shareholders.some(
    (s) =>
      s.createdAt !== undefined &&
      diffDays(s.createdAt, Date.now()) < 2 &&
      (s.calculatedShares === undefined ||
        Object.keys(s.calculatedShares).length === 0 ||
        Object.values(s.calculatedShares).reduce((a, b) => a + b) === 0),
  );
}

export function sortOwnership(res: CompanySubsidiary[]) {
  return res.sort((a, b) => {
    if (!isNil(b.ownershipInPercent) && !isNil(a.ownershipInPercent)) {
      return b.ownershipInPercent - a.ownershipInPercent;
    } else {
      return 0;
    }
  });
}

export function filterOwnership(
  trankSubsidiaries: CompanySubsidiary[],
  threshold: number,
  level: number,
) {
  return trankSubsidiaries
    .filter((subsidiary) => level === 0 || subsidiary.level === level)
    .filter(
      (subsidiary) =>
        threshold === 0 ||
        (subsidiary.ownershipInPercent !== undefined && subsidiary.ownershipInPercent >= threshold),
    );
}

const TRANSACTION_SORT_ORDER: Array<keyof Transaction> = ["transactionDate", "transactionlogId"];

export enum OwnershipGraphType {
  owner = "owner",
  owns = "owns",
  dual = "dual",
}

export const ownershipGraphTypeLabels: { [key in OwnershipGraphType]: TMessage } = {
  [OwnershipGraphType.owner]: { key: "ownership.mode.owner" },
  [OwnershipGraphType.owns]: { key: "ownership.mode.subsidiaries" },
  [OwnershipGraphType.dual]: { key: "ownership.mode.dual" },
};

export interface ShareholderDetailsState {
  isLoading?: boolean;
  shareholder?: ShareholderDetailsResponseDTO;
  allShareholders: ExtendedShareholderDTO[];
  filteredShareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  currency: string;
  canLogOnWithBankId?: boolean;
  canLogOnWithPassword?: boolean;
  shareholderStatus?: ShareholderStatus;
  isDeleting?: boolean;
  isDownloadingCertificate?: boolean;
}

export const toShareholderDetailsState = ({
  store,
  allShareholders,
  filteredShareholders,
  shareTypes,
  isLoading,
  shareholderStatus,
  isDeleting,
}: {
  store: ShareholderDetailsStore;
  allShareholders: ExtendedShareholderDTO[];
  filteredShareholders: ExtendedShareholderDTO[];
  shareTypes: ShareType[];
  isLoading?: boolean;
  shareholderStatus: ShareholderStatus;
  isDeleting?: boolean;
}): ShareholderDetailsState => ({
  isLoading: !store.shareholderDetails || isLoading,
  shareholder: store.shareholderDetails,
  allShareholders,
  filteredShareholders,
  shareTypes,
  currency: store.shareholderDetails?.shareInformation.currency ?? "",
  canLogOnWithBankId: store.canLogOnWithBankId,
  canLogOnWithPassword: store.hasUsername && store.hasPassword,
  shareholderStatus,
  isDeleting,
});

export default class ShareholdersStore {
  renderOnlyShareholdersWithIncompleteData = false;
  renderOnlyActiveShareholders = false;
  isRunAutoimport = false;
  sortedBy: keyof ExtendedShareholderDTO | undefined;
  sortedByType: string | undefined = undefined;
  sortDirection: "asc" | "desc" = "desc";
  nameFilter: StringFieldModel;
  shareholdersForEdit: ShareholderModel[] = [];
  addedShareholderId?: number;
  shareholders: ExtendedShareholderDTO[] = [];
  followUps: ShareholderFollowUpItem[] = [];
  shareTypes: ShareType[] = [];
  shareTypeCodes: string[] = [];
  calculatedSharesBySharetypeCode: {
    [shareTypeCode: string]: number;
  } = {};
  calculatedVotesBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  } = {};
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  } = {};
  transactionLog: Transaction[] = [];
  lastTransactionDate = "";
  detailedTransaction?: Transaction = undefined;
  isLoading = false;
  isEditMode = false;
  transactionsError?: TMessage;
  shareholdersCreationSuccess = false;
  transactionCreationSuccess = false;
  shareholderDetailsStore: ShareholderDetailsStore;
  exportStatus: "init" | "loading" | "success" | "error" = "init";
  readonly downloadService: DownloadService;
  trankMap: Result<string, TMessage> = pending();
  trankSubsidiaries: Result<CompanySubsidiary[], TMessage> = pending();
  trankSubsidiariesMap: Result<string, TMessage> = pending();
  downloadAsPdfResult?: Result<void, TMessage>;
  parsedExcelResponse: ParseExcelResponse | undefined = undefined;
  showActiveShareholdersFlag = true;
  showNotifyShareholdersDialog = false;
  importExcelConfig?: FileUploadConfig;

  lastUpdateResponse: UpdateShareholderFieldResponse | undefined;
  shareholderUpdateResult?: Result;
  lastSuccessfulUpdate: Date | undefined;
  validation: ValidationResultForShareholderRegister | undefined;

  autoimportResult: Result<TMessage, TMessage> | undefined = undefined;
  orgNo?: string;
  shareholdersResult: Result = pending();
  uploadShareholdersResult?: Result;
  importShareholdersResult?: Result;
  transactionLogResult: Result = pending();
  deleteResult?: Result<TMessage, TMessage>;
  deleteShareholderResult?: Result;
  changeDateResult?: Result<TMessage, TMessage>;
  changeNominalValueResult?: Result<TMessage, TMessage>;
  showDeleteTransactionDialog = false;
  showChangeNominalValueDialog = false;
  shareholderStatusFilter: ShareholderStatus = "current";

  hideAutoImportSectionResult?: Result<void, TMessage>;
  showAutoImportSectionResult?: Result<void, TMessage>;
  selectedTransaction?: Transaction;
  importShareholderIdShares: Array<ShareholderIdShares> = [];
  shareholdingImportModel?: ShareholdingPerDateModel;
  issueImportModel?: IssueOfNewSharesTransactionModel;
  notifyShareholdersStore?: NotifyShareholdersStore;
  shareTypeFilter: RadioFieldModel<ShareTypeFilter>;

  followUpDialogModel?: OptionFieldModel<ShareholderFollowUpItem>;
  isSubmittingFollowUpDialog = false;

  constructor(
    readonly history: History,
    readonly apiService: ApiService,
    readonly http: AxiosInstance,
    readonly messageStore: AlertMessageStore,
    readonly userStore: UserStore,
    readonly dialogStore: DialogStore,
  ) {
    this.nameFilter = new StringFieldModel();
    this.shareholderDetailsStore = new ShareholderDetailsStore();
    this.downloadService = new DownloadService(http);
    this.shareTypeFilter = this.initShareTypeFilter();
    makeAutoObservable(
      this,
      { http: false, history: false, apiService: false },
      { autoBind: true },
    );
  }

  getShareholderDetailsState(): ShareholderDetailsState {
    return toShareholderDetailsState({
      store: this.shareholderDetailsStore,
      allShareholders: this.shareholders,
      filteredShareholders: this.sortedShareholders,
      shareTypes: this.shareTypes,
      shareholderStatus: this.shareholderStatusFilter,
      isDeleting: isPending(this.deleteShareholderResult),
    });
  }

  get activeShareholdersForEdit(): ShareholderModel[] {
    return this.shareholdersForEdit.filter((s) =>
      hasShares(this.shareholders.find((ss) => ss.id === s.id)),
    );
  }

  get activeShareTypes(): ShareType[] {
    return this.shareTypes.filter((s) => !s.historical);
  }

  get firstNotRegisteredIssueDate(): string | undefined {
    return sortBy(this.transactionLog, (t) => t.transactionDate).find((t) =>
      isNotRegisteredIssue(t),
    )?.transactionDate;
  }

  get emptyTransactionLog() {
    return this.transactionLog.length === 0;
  }

  get emptyLogDueToNorwegianCompanyCreatedCurrentYear() {
    const currentYear = new Date().getFullYear();
    const companyCreatedAt = when(this.userStore.companyInfo?.company.created, toDate);
    const company = this.userStore.companyInfo?.company;
    return (
      currentYear === companyCreatedAt?.getFullYear() &&
      isCountryCodeNorway(company?.country) &&
      this.emptyTransactionLog
    );
  }

  get renderAutoImportSection() {
    return (
      this.transactionLog.length === 1 &&
      this.transactionLog[0].transactionType === TransactionType.AUTOIMPORT &&
      !this.userStore.companyInfo?.company.confirmedAutoImport
    );
  }

  get renderAutoImportSectionLink() {
    return (
      this.transactionLog.length === 1 &&
      this.transactionLog[0].transactionType === TransactionType.AUTOIMPORT &&
      this.userStore.companyInfo?.company.confirmedAutoImport
    );
  }

  get firstTransactionDate(): string | undefined {
    if (!this.renderAutoImportSection) {
      return undefined;
    } else {
      return this.transactionLog[0].transactionDate;
    }
  }

  get hasImportTransaction() {
    return this.shareholdingImportModel !== undefined || this.issueImportModel !== undefined;
  }

  get isSaving() {
    return isPending(this.shareholderUpdateResult);
  }

  *hideAutoImportSection() {
    this.hideAutoImportSectionResult = pending();
    try {
      yield this.apiService.markAutoImportMessageAsRendered(this.userStore.orgNumber);
      this.hideAutoImportSectionResult = ok();
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      this.hideAutoImportSectionResult = failure({
        key: "transactions.only-autoimport.hide.error",
      });
    }
  }

  *downloadAsPdf(threshold: number) {
    this.downloadAsPdfResult = pending();
    try {
      yield this.downloadService.downloadTrankMapAsPDF(this.userStore.orgNumber, threshold);
      this.downloadAsPdfResult = ok();
    } catch (err) {
      this.downloadAsPdfResult = failure({ key: "dashboard.shareholder-map.error" });
    }
  }

  get isLoadingHideAutoImport() {
    return isPending(this.hideAutoImportSectionResult);
  }

  *showAutoImportSection() {
    this.showAutoImportSectionResult = pending();
    try {
      yield this.apiService.markAutoImportMessageAsNotRendered(this.userStore.orgNumber);
      this.showAutoImportSectionResult = ok();
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      this.showAutoImportSectionResult = failure({
        key: "transactions.only-autoimport.show.error",
      });
    }
  }

  get currency(): string {
    return this.userStore.companyInfo?.company?.currency ?? CURRENCY_NOK;
  }

  get country(): string {
    return this.userStore.companyInfo?.company?.country ?? norway;
  }

  get isImportBlocked() {
    return this.parsedExcelResponse === undefined || isPending(this.importShareholdersResult);
  }

  initImportShareholdersEdit() {
    const shareholders = this.parsedExcelResponse?.parsed?.map((p) => p.shareholder) ?? [];
    this.shareholdersForEdit = shareholders.map((s) => new ShareholderModel(s, false));
    this.shareholdersForEdit.forEach((s) => s.validate(this.shareholdersForEdit));
  }

  validateActiveShareholders(callBackOnInvalid: () => void) {
    this.validation = validateAllShareholders(this.activeShareholdersForEdit);
    if (!this.validation.isValid) {
      callBackOnInvalid();
    }
  }

  validateShareholders(shareholdersForValidation: ShareholderModel[]) {
    validateAllShareholders(shareholdersForValidation);
  }

  get shareholdersForValidation(): ShareholderModel[] {
    return this.showActiveShareholders ? this.activeShareholdersForEdit : this.shareholdersForEdit;
  }

  addNewShareholderForEdit(data: ExtendedShareholderDTO) {
    this.addedShareholderId = data.id;
    this.shareholdersForEdit.push(new ShareholderModel(data, false));
    this.renderOnlyActiveShareholders = false;
    this.renderOnlyShareholdersWithIncompleteData = false;
  }

  *addPersonShareholder() {
    const shareholderId: number = this.isEditMode
      ? yield this.addNewShareholder(ShareholderType.person)
      : -this.shareholdersForEdit.length - 1;
    this.addNewShareholderForEdit({
      id: shareholderId,
      data: [],
      email: "",
      name: "",
      personInfo: emptyPersonInfo,
      isCompany: false,
      organizationNumber: this.userStore.orgNumber,
      address: { country: norway },
      calculatedShares: {},
      calculatedVotes: {},
    });
  }

  *addCompanyShareholder() {
    const shareholderId: number = this.isEditMode
      ? yield this.addNewShareholder(ShareholderType.company)
      : -this.shareholdersForEdit.length - 1;

    this.addNewShareholderForEdit({
      id: shareholderId!,
      contactInfo: emptyPersonInfo,
      isCompany: true,
      organizationNumber: this.userStore.orgNumber,
      address: { country: norway },
      calculatedShares: {},
      calculatedVotes: {},
      data: [],
      email: "",
      name: "",
    });
  }

  get shareholdersForEditing(): ShareholderModel[] {
    let shareholders = this.shareholdersForEdit;
    if (this.renderOnlyActiveShareholders) {
      shareholders = shareholders.filter((s) => s.hasShares);
    }
    if (this.renderOnlyShareholdersWithIncompleteData) {
      return shareholders.filter((m) => !m.isValid);
    } else {
      return shareholders;
    }
  }

  get isTotalOverview(): boolean {
    return this.shareTypeFilter.value === "overview";
  }

  get filteredShareTypes(): ShareType[] {
    return this.activeShareTypes.filter(
      ({ sharetypeCode }) =>
        this.shareTypeFilter.value === "overview" ||
        this.shareTypeFilter.value === "all" ||
        this.shareTypeFilter.value === "votes" ||
        sharetypeCode === this.shareTypeFilter.value,
    );
  }

  get sortedShareholders() {
    return getSortedShareholders(
      this.filteredShareholders,
      this.sortedBy,
      this.sortedByType,
      this.sortDirection,
    );
  }

  get filteredShareholders(): ExtendedShareholderDTO[] {
    const shareholders = filterShareholdersByStatus(
      this.shareholders,
      this.shareholderStatusFilter,
    );
    const nameFilter = this.nameFilter.inputValue.toLowerCase();
    return nameFilter
      ? shareholders.filter((item) => item.name.toLowerCase().includes(nameFilter))
      : shareholders;
  }

  get showEmptyStateForFilteredShareholders(): boolean {
    return (
      this.shareholderStatusFilter !== "all" &&
      this.hasShareholders &&
      this.sortedShareholders.length === 0
    );
  }

  descriptionMessage(t: TFunction): string {
    const statusKey =
      this.shareholderStatusFilter === "former"
        ? "shareholders.showActiveShareholders.former"
        : "shareholders.showActiveShareholders.current";
    return t("shareholders.showActiveShareholders-description", {
      status: t(statusKey).toLowerCase(),
    });
  }

  setSorting(
    name: keyof ExtendedShareholderDTO | undefined,
    shareTypeName: string | undefined,
    sortDirection: "asc" | "desc",
  ) {
    this.setSortDirection(sortDirection);
    this.setSortedBy(name);
    this.setSortedByType(shareTypeName);
  }

  setSortedBy(sortedBy: keyof ExtendedShareholderDTO | undefined) {
    this.sortedBy = sortedBy;
  }

  setSortedByType(sortedByType: string | undefined) {
    this.sortedByType = sortedByType;
  }

  setSortDirection(sortDirection: "asc" | "desc") {
    this.sortDirection = sortDirection;
  }

  autoimport() {
    this.isRunAutoimport = !this.isRunAutoimport;
  }

  *runAutoimport() {
    try {
      this.autoimportResult = pending();
      yield this.apiService.runAutoimport(this.userStore.orgNumber);
      this.autoimportResult = ok({ key: "shareholders.autoimport.successRunningAutoimport" });
    } catch (e) {
      this.autoimportResult = failure({ key: "shareholders.autoimport.errorRunningAutoimport" });
    }
  }

  toggleRenderOnlyShareholdersWithIncompleteData() {
    this.renderOnlyShareholdersWithIncompleteData = !this.renderOnlyShareholdersWithIncompleteData;
  }

  toggleRenderOnlyActiveShareholders() {
    this.renderOnlyActiveShareholders = !this.renderOnlyActiveShareholders;
  }

  get shareSumByType(): { [shareType: string]: number } {
    return getShareSumByType(this.shareholders);
  }

  openEditAllMode() {
    this.setEditMode(true);
    this.clearMessages();
    this.shareholderStatusFilter = "all";
    this.renderOnlyShareholdersWithIncompleteData = false;
    this.renderOnlyActiveShareholders = false;
    this.isRunAutoimport = false;
  }

  openEditIncompleteShareholders() {
    this.openEditAllMode();
    this.renderOnlyShareholdersWithIncompleteData = true;
    this.renderOnlyActiveShareholders = true;
  }

  resetOnCompanyChange() {
    this.setEditMode(false);
  }

  openAddNewShareClassDialog(
    locale: Language,
    http: AxiosInstance,
    onNewShareTypeCreated: (newShareType: ShareType) => void,
  ) {
    this.dialogStore.openAddNewShareClassDialog({
      shareholderStore: this,
      locale: locale,
      http: http,
      onFinish: (newShareType) => {
        this.shareTypes.push(newShareType);
        onNewShareTypeCreated(newShareType);
      },
    });
  }

  openActorSelectorDialog({
    title,
    excludeActors,
    onSubmit,
    isMultiSelect,
  }: {
    title: TMessage;
    excludeActors: string[];
    onSubmit: (result: SelectedActor[], onsuccess: () => void, onError: () => void) => void;
    isMultiSelect?: boolean;
  }) {
    this.dialogStore.openActorSelectorDialog({
      title,
      organisationNumber: this.userStore.orgNumber,
      tabs: [ActorType.SHAREHOLDER, ActorType.BOARD_MEMBER, ActorType.CEO],
      excludeActors,
      onLoad: this.loadActors,
      onSubmit,
      isMultiSelect,
    });
  }

  get excludeAllShareholdersForEditAsActors() {
    return this.shareholdersForEdit.map(
      (shareholder) =>
        (shareholder.isCompany
          ? shareholder.companyNumber
          : shareholder.personInfo?.socialSecurityNumber) ?? toShareholderReference(shareholder.id),
    );
  }

  get excludeAllShareholdersAsActors() {
    return this.shareholders.map(
      (shareholder) =>
        (shareholder.isCompany
          ? shareholder.companyNumber
          : shareholder.personInfo?.socialSecurityNumber) ?? toShareholderReference(shareholder.id),
    );
  }

  loadActors(
    type: ActorType,
    onSuccess: (result: Actor[]) => DCEvent[],
    onFailure: () => DCEvent[],
  ) {
    switch (type) {
      case ActorType.SHAREHOLDER:
        this.dialogStore.dispatch([
          actorsApi.getShareholders({ orgNo: this.userStore.orgNumber, onSuccess, onFailure }),
        ]);
        break;
      case ActorType.BOARD_MEMBER:
      case ActorType.CEO:
        this.dialogStore.dispatch([
          actorsApi.getCompanyRoles({
            orgNo: this.userStore.orgNumber,
            type,
            onSuccess,
            onFailure,
          }),
        ]);
        break;
      default:
        onSuccess([]);
    }
  }

  *addShareholdersFromActors(
    actors: SelectedActor[],
    onSuccess: (shareholders: ExtendedShareholderDTO[]) => void,
    onError: () => void,
  ) {
    try {
      const result: ExtendedShareholderDTO[] = yield this.apiService.addShareholdersFromActors(
        this.userStore.orgNumber,
        actors,
      );
      onSuccess(result.map((s) => transformShareholder(s)));
    } catch (e) {
      onError();
    }
  }

  openEditShareholderDialog({
    locale,
    shareholderId,
    onSuccess,
  }: {
    locale: Language;
    onSuccess: (shareholder?: ExtendedShareholderDTO) => void;
    shareholderId?: number;
  }) {
    const orgNumber = this.userStore.orgNumber;
    this.dialogStore.openEditShareholderDialog({
      orgNumber,
      locale,
      shareholderId,
      onSuccess: onSuccess,
      loadShareholders: () => this.apiService.fetchShareholdersAsActors(orgNumber),
    });
  }

  navigateToCreateShareholdingPerDateTransactionType() {
    navigateToNewTransaction(
      this.userStore.orgNumber,
      this.history,
      TransactionType.SHAREHOLDING_PER_DATE,
    );
  }

  navigateToIncorporationTransactionType() {
    navigateToNewTransaction(this.userStore.orgNumber, this.history, TransactionType.INCORPORATION);
  }

  navigateToPaymentWizard() {
    this.userStore.openPaymentDialog();
  }

  openExportShareholdersDialog() {
    this.dialogStore.openExportShareholdersDialog({
      onSubmit: this.handleSubmitExport,
      selectedContext: this.userStore.orgNumber,
    });
  }

  *handleSubmitExport(data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) {
    if (data.exportFormat === ExportFormat.HTML) {
      window.open(
        `/api/shareholder/export/${this.userStore.orgNumber}?language=${data.language}&format=${data.format}&exportFormat=${data.exportFormat}&date=${data.date}`,
      );
      onSuccess();
    } else {
      yield this.exportShareholders(data, onSuccess, onFailure);
    }
  }

  // same as `shareSumByType` but without own company shares
  get shareSumWithoutCompany(): { [shareType: string]: number } {
    return getShareSumWithoutCompany(
      this.userStore.orgNumber,
      this.shareholders,
      this.shareSumByType,
    );
  }

  get shareholdersById(): Record<number, ExtendedShareholderDTO | undefined> {
    return this.shareholders.reduce((byId, s) => ({ [s.id]: s, ...byId }), {});
  }

  get shareholderData() {
    return {
      shareholders: this.shareholders,
      shareTypes: this.shareTypes,
      shareTypeCodes: this.shareTypeCodes,
      shareSumByType: this.shareSumByType,
      shareSumWithoutCompany: this.shareSumWithoutCompany,
      trankMap: this.trankMap,
    };
  }

  get isCurrentCompanySwedish(): boolean {
    return isSwedishOrgNo(this.userStore.orgNumber);
  }

  get hasInvalidShareholders(): boolean {
    return this.validation !== undefined && !this.validation.isValid;
  }

  get hasMissingShareholders() {
    return (
      this.userStore.companyInfo &&
      this.userStore.companyInfo?.numberOfSharesWithMissingOwner > 0 &&
      this.hasAutoImportOnly
    );
  }

  get hasAutoImportOnly() {
    return (
      this.transactionLog.length === 1 &&
      this.transactionLog[0].transactionType === TransactionType.AUTOIMPORT
    );
  }

  get invalidShareholders(): number {
    return (
      this.validation?.validationsForShareholders?.filter(
        (s: ValidationResultForShareholder) => !s.isValid,
      )?.length ?? 0
    );
  }

  storeName(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.shareholderName, s.nameModel);
  }

  storeContactInfoName(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.contactInfoName, s.contactInfoNameModel);
  }

  storeContactInfoNationalId(s: ShareholderModel) {
    this.storeShareholderField(
      s,
      ShareholderField.contactInfoNationalId,
      s.contactInfoNationalIdModel,
    );
  }

  storeEmail(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.email, s.emailModel);
  }

  *storeGender(s: ShareholderModel) {
    if (this.isEditMode) {
      yield this.postShareholderField(
        s.shareholderId,
        ShareholderField.gender,
        s.genderModel.value ? s.genderModel.value : "",
      );
    }
    s.validate(this.shareholdersForValidation);
  }

  *storeIsEmployee(s: ShareholderModel) {
    if (this.isEditMode) {
      yield this.postShareholderField(
        s.shareholderId,
        ShareholderField.isEmployee,
        s.isEmployeeModel.value === null ? "" : s.isEmployeeModel.value.toString(),
      );
    }
    s.validate(this.shareholdersForValidation);
  }

  storePhone(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.phone, s.phoneModel);
  }

  storeNationalId(s: ShareholderModel) {
    s.nationalIdModel.setError(undefined);
    s.validateNationalId();
    s.validateDuplicateNationalId(this.shareholdersForValidation);
    this.storeShareholderField(s, ShareholderField.nationalId, s.nationalIdModel);
  }

  storeNationality(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.nationality, s.nationalityModel);
  }

  storeCompanyNumber(s: ShareholderModel) {
    s.companyNumberModel.setError(undefined);
    if (s.isCompany) {
      s.companyNumberModel.setAndCommitInputValue(removeAllSpaces(s.companyNumberModel.inputValue));
      if (s.nationalityModel.inputValue === norway) {
        if (s.companyNumberModel.validatedValue.length !== 9) {
          s.companyNumberModel.setError({ key: "invalidNorwegianOrgNo" });
        } else {
          this.storeShareholderField(s, ShareholderField.companyNumber, s.companyNumberModel);
        }
      } else {
        this.storeShareholderField(s, ShareholderField.companyNumber, s.companyNumberModel);
      }
      this.updateCompanyDataFromSuggestedCompany(s);
    }
  }

  updateCompanyDataFromSuggestedCompany = async (s: ShareholderModel) => {
    try {
      if (s.isValidCompanyNumber() && s.postalCodeModel.isEmpty && s.cityModel.isEmpty) {
        const companies = await this.apiService.getCompanies(s.companyNumberModel.validatedValue);
        if (companies.length === 1) {
          const company = companies[0];
          if (company.zip && company.city) {
            s.postalCodeModel.setAndCommitInputValue(company.zip);
            s.cityModel.setAndCommitInputValue(company.city);
            this.storePostalCode(s);
            this.storeCity(s);
          }
        }
      }
    } catch (e) {
      // no action
    }
  };

  storeUtlNumber(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.utlNumber, s.utlNumberModel);
  }

  storeLockupEndDate(s: ShareholderModel): void {
    this.storeShareholderField(s, ShareholderField.lockupEndDate, s.lockupEndDateModel);
  }

  storeLoanAmount(s: ShareholderModel): void {
    this.storeShareholderField(s, ShareholderField.loanAmount, s.loanAmountModel);
  }

  storeIssuedShareholderCertificate(s: ShareholderModel, checked: boolean): void {
    s.issuedShareholderCertificate.setInputValue(String(checked));
    s.issuedShareholderCertificate.commit();
    this.storeShareholderField(
      s,
      ShareholderField.issuedShareholderCertificate,
      s.issuedShareholderCertificate,
    );
  }

  storeAddress(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.address, s.addressModel);
  }

  storePostalCode(s: ShareholderModel) {
    if (s.postalCodeModel.validate()) {
      if (s.countryModel.inputValue === norway) {
        const city = lookupNorwegianCity(s.postalCodeModel.inputValue);
        if (city !== undefined) {
          s.cityModel.setAndCommitInputValue(city);
          this.storeCity(s);
        }
      }
      this.storeShareholderField(s, ShareholderField.postalCode, s.postalCodeModel);
    }
  }

  storeCity(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.city, s.cityModel);
  }

  storeCountry(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.country, s.countryModel);
  }

  storeBankDetailsCountry(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.bankDetailsCountry, s.bankDetailsCountryModel);
  }

  storeBic(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.bic, s.bicModel);
  }

  storeIban(s: ShareholderModel) {
    this.storeShareholderField(s, ShareholderField.iban, s.ibanModel);
  }

  *storeShareholderField(
    s: ShareholderModel,
    field: ShareholderField,
    model: StringFieldModel | LocalDateFieldModel | NumberFieldModel,
  ) {
    this.clearMessages();
    if (model.validate()) {
      let value: string;
      if (model instanceof NumberFieldModel) {
        value = model.parsedValue?.toString() ?? "";
      } else {
        value = model.validatedValue?.toString() ?? "";
      }
      if (this.isEditMode) yield this.postShareholderField(s.shareholderId, field, value);
      s.validate(this.shareholdersForValidation);
    }
  }

  *deleteShareholderForEdit(shareholderId: number) {
    const removeShareholderForEdit = () => {
      const index = this.shareholdersForEdit.findIndex((s) => s.shareholderId === shareholderId);
      if (index > -1) {
        this.shareholdersForEdit.splice(index, 1);
      }
    };

    if (this.isEditMode) {
      yield this.deleteShareholder(shareholderId, removeShareholderForEdit);
    } else removeShareholderForEdit();
  }

  *deleteShareholder(shareholderId: number, onSuccess?: () => void) {
    this.clearMessages();
    try {
      this.deleteShareholderResult = pending();
      yield this.apiService.deleteShareholderHard(shareholderId);
      onSuccess?.();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.deleting" });
    } finally {
      this.deleteShareholderResult = undefined;
    }
  }

  *postShareholderField(shareholderId: number, field: ShareholderField, value: string) {
    this.shareholderUpdateResult = pending();
    try {
      const response: UpdateShareholderFieldResponse = yield this.apiService.storeShareholderField(
        shareholderId,
        field,
        value,
      );
      yield new Promise((resolve) => setTimeout(resolve, 1000));
      if (response.success) {
        this.lastSuccessfulUpdate = new Date();
      } else {
        this.messageStore.showError({
          key: "shareholder.updateError",
          params: { error: validationErrorMessage[response.validationError] },
        });
      }
      this.lastUpdateResponse = response;
      this.shareholderUpdateResult = ok();
    } catch (e) {
      this.shareholderUpdateResult = failure();
      this.messageStore.showError({ key: "shareholders.error.storing" });
    } finally {
      this.setLoading(false);
    }
  }

  private addNewShareholder = async (shareholderType: ShareholderType): Promise<number> => {
    try {
      const newShareholder: NewShareholderResponse = await this.apiService.addNewShareholder(
        this.userStore.orgNumber,
        shareholderType,
      );
      return newShareholder.shareholderId;
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.storing" });
      return Promise.reject(e);
    } finally {
      this.setLoading(false);
    }
  };

  *uploadShareholderExcel(files: File[] | null) {
    if (!files) return false;
    const formData = new FormData();
    for (const file of files) {
      formData.append("file", file);
    }
    try {
      this.uploadShareholdersResult = pending();
      this.parsedExcelResponse = yield this.apiService.parseShareholdersExcel(
        this.userStore.orgNumber,
        formData,
      );
      this.initImportShareholdersEdit();
      this.uploadShareholdersResult = ok();
    } catch (e) {
      this.uploadShareholdersResult = failure();
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  *uploadShareholderExcelForUpdate(files: File[] | null) {
    if (!files) return false;
    const formData = new FormData();
    for (const file of files) {
      formData.append("file", file);
    }
    try {
      this.shareholderUpdateResult = pending();
      const response: UploadShareholdersExcelResponse =
        yield this.apiService.uploadShareholdersExcel(this.userStore.orgNumber, formData);
      this.shareholderUpdateResult = ok();

      this.messageStore.showSuccess({
        key: "shareholders.upload.success",
        params: { count: response.shareholderIds.length },
      });

      yield this.loadAllShareholders();
    } catch (e) {
      this.shareholderUpdateResult = failure();
      this.messageStore.showError({ key: "shareholders.error.update" });
    } finally {
      this.setLoading(false);
    }
  }

  initImport() {
    this.uploadShareholdersResult = undefined;
    this.importShareholdersResult = undefined;
    this.parsedExcelResponse = undefined;
    this.shareholdingImportModel = undefined;
    this.issueImportModel = undefined;
    this.renderOnlyActiveShareholders = false;
    this.renderOnlyShareholdersWithIncompleteData = false;
  }

  *importShareholders() {
    this.shareholdersForEdit.forEach((s) => s.validate(this.shareholdersForEdit));

    const shareholders = this.shareholdersForEdit.map((s) =>
      s.prepareImportData(this.parsedExcelResponse?.parsed ?? []),
    );

    try {
      this.importShareholdersResult = pending();
      this.importShareholderIdShares = yield this.apiService.importShareholders(
        this.userStore.orgNumber,
        shareholders,
      );
      this.importShareholdersResult = ok();

      const isImportingShares = this.importShareholderIdShares.some(
        (s) => s.shares !== undefined && s.shares.length > 0,
      );

      if (!isImportingShares) {
        navigateToShareholdersList(this.userStore.orgNumber, this.history);
        this.messageStore.showSuccess(
          {
            key: "shareholderImport.success",
            params: { count: shareholders.length },
          },
          { autoHide: false },
        );
      } else if (this.transactionLog.length === 0) {
        yield this.loadAllShareholders();

        this.shareholdingImportModel = new ShareholdingPerDateModel(
          this.userStore.orgNumber,
          this.shareholders,
          this.shareTypes,
          this.lastTransactionDate,
          () => this.loadAllShareholders,
          this.importEntryDateByShareholderId,
          this.importShareholderIdShares,
        );
      } else {
        yield this.loadAllShareholders();

        this.issueImportModel = new IssueOfNewSharesTransactionModel(
          this,
          this.userStore,
          this.importShareholderIdShares,
        );
      }
    } catch (e) {
      this.importShareholdersResult = failure();
      this.messageStore.showError({ key: "general-error" });
    } finally {
      this.setLoading(false);
    }
  }

  *loadAllShareholders() {
    yield this.loadShareholdersForOrgNumber(this.userStore.orgNumber);
  }

  *loadShareholdersForOrgNumber(orgNumber: string) {
    const prevOrgNo = this.orgNo;
    this.orgNo = orgNumber;
    try {
      this.shareholdersResult = pending();
      this.setLoading(true);
      const shareholdersInfo: ShareholdersInfo = yield this.fetchShareholders(orgNumber);
      this.setShareholders(shareholdersInfo, { keepFilters: orgNumber === prevOrgNo });
      this.setShareholdersForEdit(shareholdersInfo.shareholders);
      this.shareholdersResult = ok();
    } catch (e) {
      this.shareholdersResult = failure();
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  private fetchShareholders(orgNo: string, timestamp?: string): Promise<ShareholdersInfo> {
    return this.apiService.fetchShareholdersAll(orgNo, timestamp);
  }

  *loadShareholdersForDate(orgNumber: string, date: string) {
    this.setLoading(true);
    const shareholdersInfo: ShareholdersInfo = yield this.fetchShareholders(orgNumber, date);
    this.setShareholders(shareholdersInfo);
    this.setLoading(false);
  }

  setShareholders(shareholdersInfo: ShareholdersInfo, options?: { keepFilters?: boolean }) {
    this.shareholders = shareholdersInfo.shareholders;
    this.shareTypes = shareholdersInfo.shareTypes;
    this.shareTypeCodes = shareholdersInfo.shareTypeCodes;
    this.calculatedSharesBySharetypeCode = shareholdersInfo.calculatedSharesBySharetypeCode;
    this.calculatedVotesBySharetypeCode = shareholdersInfo.calculatedVotesBySharetypeCode;
    this.shareSumBySharetypeCode = shareholdersInfo.calculatedSharesBySharetypeCode;
    this.followUps = shareholdersInfo.followUps;
    this.showActiveShareholdersFlag = !shouldShowInactiveShareholders(this.shareholders);
    if (!options?.keepFilters) {
      this.shareholderStatusFilter = "current";
    }
    this.importExcelConfig = shareholdersInfo.importExcelConfig;
    this.shareTypeFilter = this.initShareTypeFilter();
  }

  private initShareTypeFilter() {
    return new RadioFieldModel<ShareTypeFilter>({
      idFn: identity,
      labelFn: (v) => {
        switch (v) {
          case "overview":
            return { key: "total-overview" };
          case "all":
            return { key: "shareholders.all-shares" };
          case "votes":
            return { key: "shareholders.votes-filter" };
          default:
            return shareTypeLabel(v);
        }
      },
      options: ["overview", "all", "votes", ...this.activeShareTypes.map((s) => s.sharetypeCode)],
      value: "overview",
    });
  }

  setShareholdersForEdit(shareholders: ExtendedShareholderDTO[]) {
    this.shareholdersForEdit = shareholders.map(
      (shareholder) => new ShareholderModel(shareholder, hasShares(shareholder)),
    );
    this.addedShareholderId = undefined;
  }

  setShowNotifyShareholdersDialog(
    value: boolean | ExtendedShareholderDTO[],
    company?: CompanyItem,
  ) {
    const createStore = () =>
      new NotifyShareholdersStore(
        this.apiService,
        this.messageStore,
        company ?? {
          organisationNumber: this.userStore.orgNumber,
          companyName: this.userStore.companyInfo?.company.name ?? "",
        },
        this.userStore.user,
        new AIClient(this.http, this.orgNo),
      );
    if (typeof value === "boolean") {
      this.showNotifyShareholdersDialog = value;
      if (value) {
        this.notifyShareholdersStore = createStore();
        this.notifyShareholdersStore.initShareholdersToNotify(this.shareholders);
      } else {
        this.notifyShareholdersStore = undefined;
      }
    } else if (Array.isArray(value)) {
      this.showNotifyShareholdersDialog = true;
      this.notifyShareholdersStore = createStore();
      this.notifyShareholdersStore.initShareholdersToNotify(
        this.shareholders,
        value.map((s) => String(s.id)),
      );
    }
  }

  get importEntryDateByShareholderId() {
    return new Map(this.importShareholderIdShares.map((s) => [s.shareholderId, s.entryDate]));
  }

  get showActiveShareholders() {
    return this.showActiveShareholdersFlag;
  }

  get hasShareholders(): boolean {
    return this.shareholders.length > 0;
  }

  get activeShareholders(): ExtendedShareholderDTO[] {
    return this.shareholders.filter((s) => hasShares(s));
  }

  *exportShareholders(data: ExportShareholderData, onSuccess: () => void, onFailure: () => void) {
    try {
      const orgNumbers = data.organizationnumbers ?? [this.userStore.orgNumber];
      yield this.downloadService
        .exportShareholdersBatch(
          orgNumbers,
          data.language,
          data.format,
          data.date,
          data.exportFormat,
        )
        .then((res) => {
          const filename = getResponseFilename(res);
          downloadFile(res.data, filename);
          onSuccess();
        });
    } catch (e) {
      onFailure();
      this.messageStore.showError({ key: "shareholders.error.export" });
    }
  }

  *downloadShareholdersExcelFile() {
    yield this.downloadService.exportShareholdersData(this.userStore.orgNumber).then((res) => {
      const filename = getResponseFilename(res);
      downloadFile(res.data, filename);
    });
  }

  downloadShareholderCertificate(shareholderId: string | number, locale: Language) {
    this.setLoading(true);
    this.downloadService
      .downloadShareholderCertificate(shareholderId, locale)
      .then((response: AxiosResponse) => downloadFile(response.data, getResponseFilename(response)))
      .catch(() => {
        this.messageStore.showError({ key: "shareholders.error.downloadingCertificate" });
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  *getTransactionLog() {
    try {
      this.transactionLogResult = pending();
      this.setLoading(true);
      const transactionLog: Transaction[] = yield this.fetchTransactionLog();
      this.transactionLog = orderBy(transactionLog, TRANSACTION_SORT_ORDER, ["desc", "desc"]);
      this.lastTransactionDate = transactionLog
        .map((t) => t.transactionDate)
        .reduce((a, b) => (a > b ? a : b), "");
      this.transactionLogResult = ok();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  private fetchTransactionLog(): Promise<Transaction[]> {
    return this.apiService.fetchTransactionLog(this.userStore.orgNumber);
  }

  deleteTransaction(transaction: Transaction) {
    this.showDeleteTransactionDialog = true;
    this.selectedTransaction = transaction;
    this.deleteResult = undefined;
  }

  cancelDeleteTransaction() {
    this.showDeleteTransactionDialog = false;
    this.selectedTransaction = undefined;
    this.deleteResult = undefined;
  }

  *doDeleteTransaction(transaction: Transaction) {
    this.deleteResult = pending();
    try {
      yield this.apiService.deleteTransaction(
        this.userStore.orgNumber,
        transaction.transactionlogId,
      );
      yield this.userStore.updateCompanyInfo();
      this.deleteResult = ok({ key: "transactions.delete.success" });
      this.messageStore.showSuccess({ key: "transactions.delete.success" });
      this.showDeleteTransactionDialog = false;
      this.selectedTransaction = undefined;
      yield this.getTransactionLog();
      yield this.reloadShareholderDetails();
    } catch (e) {
      if (isBadRequestError(e)) {
        this.deleteResult = failure({ key: "transactions.delete.validation-error" });
      } else {
        this.deleteResult = failure({ key: "transactions.delete.system-error" });
      }
    }
  }

  changeTransactionDate(transaction: Transaction, infoText?: TMessage) {
    this.dialogStore.openChangeTransactionDateDialog({
      selectedTransaction: transaction,
      controller: this,
      infoText,
    });
    this.changeDateResult = undefined;
  }

  cancelChangeTransactionDate() {
    this.changeDateResult = undefined;
    this.dialogStore.closeDialog();
  }

  *doChangeTransactionDate(transaction: Transaction, date: string): Generator {
    this.changeDateResult = pending();
    try {
      yield this.apiService.changeTransactionDate(
        this.userStore.orgNumber,
        transaction.transactionlogId,
        date,
      );

      this.dialogStore.closeDialog();
      yield this.userStore.updateCompanyInfo();
      this.changeDateResult = ok({ key: "transactions.change-date.success" });
      this.messageStore.showSuccess({ key: "transactions.change-date.success" });
      yield this.getTransactionLog();
      yield this.reloadShareholderDetails();
      if (this.detailedTransaction) {
        yield this.getDetailedTransaction(this.detailedTransaction.transactionlogId);
      }
    } catch (e) {
      if (isBadRequestError(e)) {
        this.changeDateResult = failure({ key: "transactions.change-date.validation-error" });
      } else {
        this.changeDateResult = failure({ key: "transactions.change-date.system-error" });
      }
    }
  }

  changeNominalValue(transaction: Transaction) {
    this.selectedTransaction = transaction;
    this.showChangeNominalValueDialog = true;
    this.changeNominalValueResult = undefined;
  }

  cancelChangeNominalValue() {
    this.selectedTransaction = undefined;
    this.showChangeNominalValueDialog = false;
    this.changeNominalValueResult = undefined;
  }

  *doChangeNominalValue(transaction: Transaction, nominalValue: number) {
    this.changeNominalValueResult = pending();
    try {
      yield this.apiService.changeNominalValueForTransaction(
        this.userStore.orgNumber,
        transaction.transactionlogId,
        nominalValue,
      );
      this.messageStore.showSuccess({ key: "transactions.change-nominal-value.success" });
      this.changeNominalValueResult = undefined;
      this.showChangeNominalValueDialog = false;
      this.selectedTransaction = undefined;
      yield this.getTransactionLog();
      yield this.userStore.updateCompanyInfo();
    } catch (e) {
      this.changeNominalValueResult = failure({ key: "general-error" });
    }
  }

  *getDetailedTransaction(id: number | string) {
    try {
      this.setLoading(true);
      this.detailedTransaction = yield this.apiService.fetchTransactionById(id);
    } catch (e) {
      this.setTransactionsError({ key: "shareholders.error.load" });
    } finally {
      this.setLoading(false);
    }
  }

  *uploadTransactionAttachments(form: FormData) {
    if (this.detailedTransaction) {
      const id = this.detailedTransaction.transactionlogId;
      try {
        yield this.apiService.uploadTransactionAttachment(id, form);
      } catch (e) {
        // no action
      }
      yield this.getDetailedTransaction(id);
    }
  }

  *deleteTransactionAttachment(attachmentId: string) {
    if (this.detailedTransaction) {
      try {
        yield this.apiService.deleteTransactionAttachment(
          this.detailedTransaction.transactionlogId,
          attachmentId,
        );
      } catch (e) {
        // no action
      }
      yield this.getDetailedTransaction(this.detailedTransaction.transactionlogId);
    }
  }

  clearDetailedTransaction = () => {
    this.detailedTransaction = undefined;
  };

  get isDisplayContractNotesDownloadButton(): boolean {
    const transactionType = this.detailedTransaction?.transactionType;
    return transactionType === "BUY_SELL";
  }

  downloadContractNotes() {
    if (this.detailedTransaction) {
      this.setLoading(true);
      this.downloadService
        .downloadContractNote(this.detailedTransaction.transactionlogId)
        .catch((result) => {
          if (result.status === "rejected") {
            this.setTransactionsError({ key: "shareholders.error.downloadingContractNote" });
          }
        })
        .finally(() => {
          this.setLoading(false);
        });
    }
  }

  /* Can be used on page mount */
  clearMessages = (): void => {
    this.transactionsError = undefined;
    this.exportStatus = "init";
    this.shareholdersCreationSuccess = false;
    this.transactionCreationSuccess = false;
    this.shareholderUpdateResult = undefined;
  };

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  setEditMode(isEditMode: boolean) {
    this.isEditMode = isEditMode;
  }

  setTransactionsError(error?: TMessage) {
    this.transactionsError = error;
  }

  clearTrankMap() {
    this.trankMap = pending();
  }

  *downloadTrankMap(orgNumber: string, threshold: number) {
    try {
      this.trankMap = pending();
      const res: string = yield this.downloadService.downloadTrankMap(orgNumber, threshold);
      this.trankMap = ok(res);
    } catch (err) {
      this.trankMap = failure({ key: "dashboard.shareholder-map.error" });
    }
  }

  *downloadTrankSubsidiariesMap(
    threshold: number,
    maxnum: number,
    ownershipGraphType: OwnershipGraphType,
  ) {
    if (this.userStore.isShareholderView) {
      this.trankSubsidiariesMap = failure({ key: "notFound" });
    } else {
      try {
        this.trankSubsidiariesMap = pending();
        const res: string = yield this.downloadService.downloadTrankSubsidiariesMap({
          organisationNumber: this.userStore.orgNumber,
          threshold,
          maxnum,
          ownershipGraphType,
        });
        this.trankSubsidiariesMap = ok(res);
      } catch (err) {
        this.trankSubsidiariesMap = failure({ key: "ownership.map.error" });
      }
    }
  }

  *downloadTrankSubsidiariesMapAsPdf(
    threshold: number,
    maxnum: number,
    ownershipGraphType: OwnershipGraphType,
  ) {
    this.downloadAsPdfResult = pending();
    try {
      yield this.downloadService.downloadTrankSubsidiariesMapAsPDF(
        this.userStore.orgNumber,
        threshold,
        maxnum,
        ownershipGraphType,
      );
      this.downloadAsPdfResult = ok();
    } catch (err) {
      this.downloadAsPdfResult = failure({ key: "ownership.map.error" });
    }
  }

  *downloadTrankSubsidiariesAsExcel(threshold: number, maxnum: number) {
    this.downloadAsPdfResult = pending();
    try {
      yield this.downloadService.downloadTrankSubsidiariesAsExcel(
        this.userStore.orgNumber,
        threshold,
        maxnum,
      );
      this.downloadAsPdfResult = ok();
    } catch (err) {
      this.downloadAsPdfResult = failure({ key: "ownership.map.error" });
    }
  }

  *fetchTrankSubsidiaries(orgNumber: string) {
    try {
      this.trankSubsidiaries = pending();
      const res: CompanySubsidiary[] = yield this.apiService.fetchTrankSubsidiaries(orgNumber);
      this.trankSubsidiaries = ok(sortOwnership(res));
    } catch (err) {
      this.trankSubsidiaries = failure({ key: "ownership.error" });
    }
  }

  subsidiaryLevelsAvailable(): number[] {
    return isOk(this.trankSubsidiaries)
      ? Array.from(new Set(this.trankSubsidiaries.data.map((d) => d.level))).sort((a, b) => a - b)
      : [];
  }

  showCancelAutoimportButton(): boolean {
    return this.autoimportResult === undefined || isPending(this.autoimportResult);
  }

  setShareholderStatusFilter(value: ShareholderStatus) {
    this.shareholderStatusFilter = value;
  }

  *reloadShareholderDetails() {
    if (this.shareholderDetailsStore.shareholderDetails) {
      yield this.getShareholderDetails(
        this.shareholderDetailsStore.shareholderDetails.shareholderId,
      );
    }
  }

  *getShareholderDetails(id: number) {
    try {
      this.shareholderDetailsStore.setLoading(true);
      const shareholderDetails: ShareholderDetailsResponseDTO =
        yield this.apiService.fetchShareholderById(id);
      this.shareholderDetailsStore.shareholderDetails = shareholderDetails;
      this.shareholderDetailsStore.shareholderPledgeForm.setIsPledged(shareholderDetails.isPledged);
      this.shareholderDetailsStore.shareholderPledgeForm.setPledgeComment(
        shareholderDetails.pledgeComment,
      );
      this.shareholderDetailsStore.shareholderRemarksForm.setShareholderName(
        shareholderDetails.shareholderName,
      );
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.load" });
    } finally {
      this.shareholderDetailsStore.setLoading(false);
    }
  }

  *updateShareholderPledge(id: number, form: ShareholderPledgeForm) {
    form.setMode("saving");
    try {
      yield this.apiService.updateShareholderPledge(id, form);
      form.setMode("view");
      form.resetError();
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.save" });
      form.setMode("edit");
    }
  }

  *updateShareholderRemarks(id: number, form: ShareholderRemarksForm) {
    const currentMode = form.mode;
    form.setMode("saving");
    try {
      yield this.apiService.updateShareholderRemark(id, form);
      form.setMode("hide");
    } catch (e) {
      this.messageStore.showError({ key: "shareholders.error.save" });
      form.setMode(currentMode);
    }
  }

  *deleteRepresentative(id: number) {
    if (!this.shareholderDetailsStore.shareholderDetails) return;
    const { shareholderId } = this.shareholderDetailsStore.shareholderDetails;
    try {
      yield this.apiService.deleteRepresentatives(shareholderId, [id]);
      yield this.getShareholderDetails(shareholderId);
      this.messageStore.showGeneralSuccess();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }

  isTransactionLogChangedInLastDay() {
    const updateDates = this.transactionLog.map((t) => t.updatedDate);
    const latestUpdate = max(updateDates);
    return latestUpdate ? diffDays(new Date(), toDate(latestUpdate)) <= 1 : false;
  }

  openFollowUpDialog() {
    this.followUpDialogModel = new OptionFieldModel({
      selectedIds: this.followUps.map((s) => s.companyNumber),
      options: this.followUps,
      idFn: (value) => value.companyNumber,
      labelFn: (value) => ({ translated: value.companyName }),
    });
  }

  closeFollowUpDialog() {
    this.followUpDialogModel = undefined;
  }

  *submitFollowUpDialog() {
    const orgNos = this.followUpDialogModel?.selectedIds;
    if (!orgNos) return;

    this.isSubmittingFollowUpDialog = true;
    try {
      yield this.http.post("/portfolio/follow-up/shareholders/update", {
        orgNo: this.orgNo,
        shareholderOrgNos: orgNos,
      });

      this.closeFollowUpDialog();
      this.messageStore.showSuccess({
        key: "followup.shareholders.dialog-success",
        params: { count: orgNos.size },
      });

      this.loadAllShareholders();
    } catch (e) {
      this.messageStore.showGeneralError();
    }
  }
}

export class ShareholderDetailsStore {
  shareholderDetails: ShareholderDetailsResponseDTO | undefined = undefined;
  shareholderPledgeForm: ShareholderPledgeForm;
  shareholderRemarksForm: ShareholderRemarksForm;

  isLoading = false;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    this.shareholderPledgeForm = new ShareholderPledgeForm();
    this.shareholderRemarksForm = new ShareholderRemarksForm();
  }

  get hasUsername() {
    return this.shareholderDetails?.usernames && this.shareholderDetails.usernames.length > 0;
  }

  get hasPassword() {
    return this.shareholderDetails?.usernames.some((d) => d.hasPassword);
  }

  get portfolio(): ShareholderPortfolio[] {
    return this.shareholderDetails?.portfolio ?? [];
  }

  get transactionLog(): Transaction[] {
    return this.shareholderDetails?.transactionLog ?? [];
  }

  get isCompany() {
    return this.shareholderDetails?.isCompany ?? false;
  }

  get personOrContactInfo() {
    const info = this.isCompany
      ? this.shareholderDetails?.contactInfo
      : this.shareholderDetails?.personInfo;

    if (!info) return undefined;

    return {
      shareholderName: removeExtraSpaces(
        `${info.firstName} ${info.middleName ?? ""} ${info.lastName}`,
      ),
      phoneNumber: formatPhoneNumber(info.phoneNumber),
      email: info.email ?? "",
      gender: info.gender,
    };
  }

  get shareholderOrCompanyName() {
    return this.isCompany
      ? (this.shareholderDetails?.companyName ?? "")
      : (this.personOrContactInfo?.shareholderName ?? "");
  }

  updateEmail(email: string) {
    if (this.isCompany && this.shareholderDetails?.contactInfo) {
      this.shareholderDetails.contactInfo.email = email;
    } else if (this.shareholderDetails?.personInfo) {
      this.shareholderDetails.personInfo.email = email;
    }
  }

  get iban() {
    return this.shareholderDetails?.iban ?? "";
  }

  get bic() {
    return this.shareholderDetails?.bic ?? "";
  }

  get isEmployee() {
    return this.shareholderDetails?.isEmployee;
  }

  get nationality() {
    return this.shareholderDetails?.nationality ?? "";
  }

  get issuedShareholderCertificate() {
    return this.shareholderDetails?.issuedShareholderCertificate ? "yes" : "no";
  }

  get lastLogin() {
    return this.shareholderDetails?.lastLogin;
  }

  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  resetShareholder() {
    this.shareholderDetails = undefined;
    this.shareholderPledgeForm.resetForm();
  }

  get personInfoOrContactInfo(): PersonInfo | undefined {
    if (this.shareholderDetails?.isCompany) {
      return this.shareholderDetails?.contactInfo;
    } else {
      return this.shareholderDetails?.personInfo;
    }
  }

  get supportedBankIdCountry(): boolean {
    const country = this.shareholderDetails?.shareholderAddress?.country;
    return isCountryCodeSweden(country) || isCountryCodeNorway(country);
  }

  get canLogOnWithBankId(): boolean {
    return (
      this.supportedBankIdCountry && isNotBlank(this.personInfoOrContactInfo?.socialSecurityNumber)
    );
  }
}

export function hasShares(s: ExtendedShareholderDTO | undefined): boolean {
  return s !== undefined && Object.values(s.calculatedShares).some((c) => c > 0);
}

export function filterActiveShareholders(
  shareholders: ExtendedShareholderDTO[],
  showActiveShareholders = true,
): ExtendedShareholderDTO[] {
  return shareholders.filter((s) => {
    return !showActiveShareholders || hasShares(s);
  });
}

export function filterShareholdersByStatus(
  shareholders: ExtendedShareholderDTO[],
  status: ShareholderStatus,
): ExtendedShareholderDTO[] {
  switch (status) {
    case "all":
      return shareholders;
    case "current":
      return shareholders.filter((s) => hasShares(s));
    case "former":
      return shareholders.filter((s) => !hasShares(s));
  }
}

export function getSortedShareholders(
  filteredShareholders: ExtendedShareholderDTO[],
  sortedBy: keyof ExtendedShareholderDTO | undefined = undefined,
  sortedByType: string | undefined = undefined,
  sortDirection: "asc" | "desc" = "desc",
): ExtendedShareholderDTO[] {
  if (sortedBy) {
    return orderBy(
      filteredShareholders.map((i) => ({
        ...i,
        sortField: sortedBy === "name" ? i[sortedBy].toLowerCase() : "",
      })),
      sortedBy === "name" ? "sortField" : [sortedBy],
      [sortDirection],
    );
  }
  if (sortedByType === "totalshares" || sortedByType === "percentageShares") {
    return orderBy(
      filteredShareholders,
      [(s) => totalNumberOfSharesOfShareholder(s)],
      [sortDirection],
    );
  }
  if (sortedByType === "totalVotes" || sortedByType === "percentageVotes") {
    return orderBy(
      filteredShareholders,
      [(s) => totalNumberOfVotesForShareholder(s)],
      [sortDirection],
    );
  }
  if (sortedByType) {
    const sortedByTypeOriginal = sortedByType?.replace("-percentage", "");
    return orderBy(
      filteredShareholders,
      [(s) => s.calculatedShares[sortedByTypeOriginal as ShareTypeCode] ?? 0],
      [sortDirection],
    );
  }
  return orderBy(
    filteredShareholders,
    [(s) => totalNumberOfVotesForShareholder(s)],
    [sortDirection],
  );
}

export const emptyPersonInfo: PersonInfo = {
  phoneNumber: { value: "", countryCode: "" },
  firstName: "",
  lastName: "",
};

export function showLiquidationInfo(transactionLog: Transaction[]): boolean {
  return transactionLog.some((t) => t.transactionType === TransactionType.LIQUIDATION);
}

export type ShareTypeFilter = "overview" | "all" | "votes" | ShareTypeCode;
